import React, { useState, Fragment } from 'react';
import { ArrowLeftIcon, PlusIcon, InfoIcon, PencilIcon, CheckIcon, TrashIcon, XIcon, SearchIcon, StarIcon, TruckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { TruckingCompanyRatesModal } from './TruckingCompanyRatesModal';
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  truckCount: number;
  rating: number;
  status: 'active' | 'inactive' | 'pending';
  preferredStatus?: boolean;
  rates?: {
    [key: string]: number;
  };
}
interface ManageTruckingCompaniesPageProps {
  onBack: () => void;
}
export const ManageTruckingCompaniesPage = ({
  onBack
}: ManageTruckingCompaniesPageProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<TruckingCompany | null>(null);
  const [expandedCompany, setExpandedCompany] = useState<string | null>(null);
  // Updated truck types to match the image
  const truckTypes = ['Dump Truck 10', 'Dump Truck 12', 'Dump Truck 14+', 'Strong Arm 12', 'Strong Arm 14', 'Strong Arm 16+', 'Side Dump', 'Super Side 30tn+', 'Jumbo Side 35tn+', 'Belly Dump', 'Double Belly'];
  const [truckingCompanies, setTruckingCompanies] = useState<TruckingCompany[]>([{
    id: '1',
    name: 'Mountain View Trucking',
    contactPerson: 'Bob Wilson',
    email: 'bob@mountainviewtrucking.com',
    phone: '555-234-5678',
    truckCount: 15,
    rating: 4.7,
    status: 'active',
    preferredStatus: true,
    rates: {
      'Dump Truck 10': 85.0,
      'Dump Truck 12': 90.0,
      'Dump Truck 14+': 95.0,
      'Strong Arm 12': 100.0,
      'Strong Arm 14': 105.0,
      'Strong Arm 16+': 110.0,
      'Side Dump': 115.0,
      'Super Side 30tn+': 120.0,
      'Jumbo Side 35tn+': 125.0,
      'Belly Dump': 130.0,
      'Double Belly': 135.0
    }
  }, {
    id: '2',
    name: 'Desert Haul Express',
    contactPerson: 'Maria Garcia',
    email: 'maria@deserthaulexpress.com',
    phone: '555-345-6789',
    truckCount: 12,
    rating: 4.5,
    status: 'active',
    rates: {
      'Dump Truck 10': 80.0,
      'Dump Truck 12': 85.0,
      'Dump Truck 14+': 90.0,
      'Strong Arm 12': 95.0,
      'Strong Arm 14': 100.0,
      'Strong Arm 16+': 105.0,
      'Side Dump': 110.0,
      'Super Side 30tn+': 115.0,
      'Jumbo Side 35tn+': 120.0,
      'Belly Dump': 125.0,
      'Double Belly': 130.0
    }
  }, {
    id: '3',
    name: 'Valley Transport Co.',
    contactPerson: 'James Thompson',
    email: 'james@valleytransport.com',
    phone: '555-456-7890',
    truckCount: 8,
    rating: 4.3,
    status: 'active',
    rates: {
      'Dump Truck 10': 82.0,
      'Dump Truck 12': 87.0,
      'Dump Truck 14+': 92.0,
      'Strong Arm 12': 97.0,
      'Strong Arm 14': 102.0,
      'Strong Arm 16+': 107.0,
      'Side Dump': 112.0,
      'Super Side 30tn+': 117.0,
      'Jumbo Side 35tn+': 122.0,
      'Belly Dump': 127.0,
      'Double Belly': 132.0
    }
  }, {
    id: '4',
    name: 'Summit Hauling',
    contactPerson: 'Linda Chen',
    email: 'linda@summithauling.com',
    phone: '555-567-8901',
    truckCount: 10,
    rating: 4.8,
    status: 'active',
    rates: {
      'Dump Truck 10': 88.0,
      'Dump Truck 12': 93.0,
      'Dump Truck 14+': 98.0,
      'Strong Arm 12': 103.0,
      'Strong Arm 14': 108.0,
      'Strong Arm 16+': 113.0,
      'Side Dump': 118.0,
      'Super Side 30tn+': 123.0,
      'Jumbo Side 35tn+': 128.0,
      'Belly Dump': 133.0,
      'Double Belly': 138.0
    }
  }, {
    id: 'dirt-logics-trucking',
    name: 'Dirt Logics Trucking',
    contactPerson: 'Dirt Logics Trucking',
    email: 'trucking@dirtlogics.com',
    phone: '555-456-7890',
    truckCount: 18,
    rating: 4.9,
    status: 'active',
    preferredStatus: true,
    rates: {
      'Dump Truck 10': 88.0,
      'Dump Truck 12': 93.0,
      'Dump Truck 14+': 98.0,
      'Strong Arm 12': 103.0,
      'Strong Arm 14': 108.0,
      'Strong Arm 16+': 113.0,
      'Side Dump': 118.0,
      'Super Side 30tn+': 123.0,
      'Jumbo Side 35tn+': 128.0,
      'Belly Dump': 133.0,
      'Double Belly': 138.0
    }
  }]);
  const handleAddTruckingCompany = (newCompany: Omit<TruckingCompany, 'id'>) => {
    const id = (truckingCompanies.length + 1).toString();
    // Create default rates for all truck types
    const rates: {
      [key: string]: number;
    } = {};
    truckTypes.forEach(type => {
      rates[type] = 85.0; // Default rate
    });
    setTruckingCompanies([...truckingCompanies, {
      ...newCompany,
      id,
      rates
    }]);
    setShowAddForm(false);
  };
  const handleUpdateRates = (companyId: string, updatedRates: {
    [key: string]: number;
  }) => {
    setTruckingCompanies(truckingCompanies.map(company => company.id === companyId ? {
      ...company,
      rates: updatedRates
    } : company));
    setShowRatesModal(false);
    setSelectedCompany(null);
  };
  const handleEditRates = (company: TruckingCompany) => {
    setSelectedCompany(company);
    setShowRatesModal(true);
  };
  return <div className="p-6 w-full" data-id="element-858">
      <div className="flex justify-between items-center mb-6" data-id="element-859">
        <div className="w-1/3" data-id="element-860"></div>
        <h1 className="text-2xl font-bold text-center w-1/3" data-id="element-861">
          Manage Trucking Companies
        </h1>
        <div className="flex justify-end w-1/3" data-id="element-862">
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={onBack} data-id="element-863">
            Back to Dashboard
          </button>
        </div>
      </div>
      <div className="flex justify-between items-center mb-4" data-id="element-864">
        <div className="w-1/3" data-id="element-865"></div>
        <h2 className="text-xl font-medium text-center w-1/3" data-id="element-866">
          Your Trucking Companies
        </h2>
        <div className="flex justify-end w-1/3" data-id="element-867">
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => setShowAddForm(true)} data-id="element-868">
            <PlusIcon size={18} data-id="element-869" />
            Add Trucking Company
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-start gap-3" data-id="element-870">
        <InfoIcon size={20} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-871" />
        <div data-id="element-872">
          <div className="font-medium text-blue-800" data-id="element-873">
            Trucking Company & Rate Management
          </div>
          <p className="text-blue-700 text-sm" data-id="element-874">
            Set custom rates for each truck type offered by your trucking
            companies. Click on any company to expand and view detailed rates,
            or use "Edit Rates" to customize pricing.
          </p>
        </div>
      </div>
      {showAddForm ? <AddTruckingCompanyForm onSave={handleAddTruckingCompany} onCancel={() => setShowAddForm(false)} data-id="element-875" /> : <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-876">
          <div className="overflow-x-auto" data-id="element-877">
            <table className="w-full border-collapse" data-id="element-878">
              <thead data-id="element-879">
                <tr className="bg-gray-50 text-left" data-id="element-880">
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-881">
                    COMPANY
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-882">
                    CONTACT
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-883">
                    TRUCKS
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-884">
                    RATING
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-885">
                    STATUS
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-886">
                    PREFERRED
                  </th>
                  <th className="px-6 py-3 text-gray-600 font-medium" data-id="element-887">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" data-id="element-888">
                {truckingCompanies.map(company => <Fragment key={company.id} data-id="element-889">
                    <tr className="hover:bg-gray-50" data-id="element-890">
                      <td className="px-6 py-4" data-id="element-891">
                        <div className="flex items-center gap-2" data-id="element-892">
                          <button onClick={() => setExpandedCompany(expandedCompany === company.id ? null : company.id)} className="text-gray-400 hover:text-gray-600" data-id="element-893">
                            {expandedCompany === company.id ? <ChevronUpIcon size={16} data-id="element-894" /> : <ChevronDownIcon size={16} data-id="element-895" />}
                          </button>
                          <div className="font-medium" data-id="element-896">{company.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-897">
                        <div data-id="element-898">{company.contactPerson}</div>
                        <div className="text-sm text-gray-500" data-id="element-899">
                          {company.email}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-900">
                          {company.phone}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-901">
                        <div className="flex items-center gap-2" data-id="element-902">
                          <TruckIcon size={16} className="text-gray-400" data-id="element-903" />
                          <span data-id="element-904">{company.truckCount} trucks</span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-905">
                        <div className="flex items-center" data-id="element-906">
                          <span className="mr-2" data-id="element-907">{company.rating}</span>
                          <div className="flex" data-id="element-908">
                            {[...Array(5)].map((_, i) => <StarIcon key={i} size={16} className={i < Math.floor(company.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} data-id="element-909" />)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-910">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium" data-id="element-911">
                          {company.status === 'active' ? 'Active' : company.status === 'pending' ? 'Pending' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-912">
                        <div className="flex items-center" data-id="element-913">
                          <input type="checkbox" className="mr-2" checked={company.preferredStatus} readOnly data-id="element-914" />
                          <span className="text-sm" data-id="element-915">
                            {company.preferredStatus ? 'Preferred' : 'Standard'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-916">
                        <div className="flex gap-2" data-id="element-917">
                          <button onClick={() => handleEditRates(company)} className="text-blue-600 hover:text-blue-800 text-sm" data-id="element-918">
                            Edit Rates
                          </button>
                          <button className="text-green-600 hover:text-green-800" data-id="element-919">
                            <CheckIcon size={18} data-id="element-920" />
                          </button>
                          <button className="text-red-600 hover:text-red-800" data-id="element-921">
                            <TrashIcon size={18} data-id="element-922" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedCompany === company.id && <tr data-id="element-923">
                        <td colSpan={7} className="px-6 py-4 bg-gray-50" data-id="element-924">
                          <div className="border rounded-md bg-white" data-id="element-925">
                            <div className="px-4 py-3 bg-gray-100 border-b" data-id="element-926">
                              <h4 className="font-medium text-gray-800" data-id="element-927">
                                Truck Type Rates for {company.name}
                              </h4>
                            </div>
                            <div className="overflow-x-auto" data-id="element-928">
                              <table className="w-full text-sm" data-id="element-929">
                                <thead data-id="element-930">
                                  <tr className="bg-gray-50" data-id="element-931">
                                    {truckTypes.map(type => <th key={type} className="p-2 text-left border-r text-xs" data-id="element-932">
                                        {type.toUpperCase()}
                                      </th>)}
                                  </tr>
                                </thead>
                                <tbody data-id="element-933">
                                  <tr data-id="element-934">
                                    {truckTypes.map(type => <td key={type} className="p-2 border-r" data-id="element-935">
                                        <span className="font-medium" data-id="element-936">
                                          $
                                          {company.rates?.[type]?.toFixed(2) || 'N/A'}
                                        </span>
                                      </td>)}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="px-4 py-3 border-t bg-gray-50" data-id="element-937">
                              <button onClick={() => handleEditRates(company)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm" data-id="element-938">
                                Edit All Rates
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Trucking Company Rates Modal */}
      {showRatesModal && selectedCompany && <TruckingCompanyRatesModal company={selectedCompany} truckTypes={truckTypes} onClose={() => {
      setShowRatesModal(false);
      setSelectedCompany(null);
    }} onSave={handleUpdateRates} data-id="element-939" />}
    </div>;
};
interface AddTruckingCompanyFormProps {
  onSave: (company: Omit<TruckingCompany, 'id'>) => void;
  onCancel: () => void;
}
const AddTruckingCompanyForm = ({
  onSave,
  onCancel
}: AddTruckingCompanyFormProps) => {
  const [formData, setFormData] = useState<Omit<TruckingCompany, 'id'>>({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    truckCount: 1,
    rating: 5.0,
    status: 'active',
    preferredStatus: false
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target as HTMLInputElement;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else if (name === 'truckCount' || name === 'rating') {
      setFormData({
        ...formData,
        [name]: parseFloat(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };
  return <div className="bg-white rounded-lg shadow-md p-6" data-id="element-940">
      <div className="flex justify-between items-center mb-4" data-id="element-941">
        <h3 className="text-lg font-medium" data-id="element-942">Add New Trucking Company</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" data-id="element-943">
          <XIcon size={20} data-id="element-944" />
        </button>
      </div>
      <form onSubmit={handleSubmit} data-id="element-945">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-946">
          <div data-id="element-947">
            <label className="block text-sm font-medium mb-2" data-id="element-948">
              Company Name
            </label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter company name" required data-id="element-949" />
          </div>
          <div data-id="element-950">
            <label className="block text-sm font-medium mb-2" data-id="element-951">
              Contact Person
            </label>
            <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="Enter contact name" required data-id="element-952" />
          </div>
          <div data-id="element-953">
            <label className="block text-sm font-medium mb-2" data-id="element-954">
              Email Address
            </label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="contact@company.com" required data-id="element-955" />
          </div>
          <div data-id="element-956">
            <label className="block text-sm font-medium mb-2" data-id="element-957">
              Phone Number
            </label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" placeholder="555-123-4567" required data-id="element-958" />
          </div>
          <div data-id="element-959">
            <label className="block text-sm font-medium mb-2" data-id="element-960">
              Number of Trucks
            </label>
            <input type="number" name="truckCount" value={formData.truckCount} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" min="1" required data-id="element-961" />
          </div>
          <div data-id="element-962">
            <label className="block text-sm font-medium mb-2" data-id="element-963">Status</label>
            <select name="status" value={formData.status} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded-md" required data-id="element-964">
              <option value="active" data-id="element-965">Active</option>
              <option value="inactive" data-id="element-966">Inactive</option>
              <option value="pending" data-id="element-967">Pending</option>
            </select>
          </div>
        </div>
        <div className="mb-6" data-id="element-968">
          <label className="flex items-center" data-id="element-969">
            <input type="checkbox" name="preferredStatus" checked={formData.preferredStatus} onChange={handleChange} className="mr-2" data-id="element-970" />
            <span data-id="element-971">Mark as preferred trucking company</span>
          </label>
          <p className="text-sm text-gray-500 mt-1" data-id="element-972">
            Preferred companies will be suggested first when assigning jobs
          </p>
        </div>
        <div className="flex justify-end gap-3" data-id="element-973">
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-974">
            Cancel
          </button>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-975">
            Add Trucking Company
          </button>
        </div>
      </form>
    </div>;
};